// src/pages/seller/SellerReviewsPage.jsx
import React, { useState, useEffect } from 'react';
import { Star, MessageCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { db } from '../../utils/firebase';
import {
  collection,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';

const SellerReviewsPage = () => {
  const { user } = useApp();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productMap, setProductMap] = useState({});

  useEffect(() => {
    if (!user?.uid || user.userType !== 'seller') {
      setLoading(false);
      setError('Access denied. Seller account required.');
      return;
    }

    const fetchReviews = async () => {
      try {
        // 1. 판매자의 제품 목록 가져오기
        const productsRef = collection(db, 'products');
        const q = query(productsRef, where('sellerId', '==', user.uid));
        const unsubscribeProducts = onSnapshot(q, async (productSnap) => {
          const sellerProductIds = [];
          const productData = {};
          productSnap.docs.forEach(doc => {
            const p = doc.data();
            sellerProductIds.push(doc.id);
            productData[doc.id] = { name: p.name };
          });

          if (sellerProductIds.length === 0) {
            setReviews([]);
            setLoading(false);
            return;
          }

          // 2. 해당 제품들의 댓글 가져오기
          const commentsRef = collection(db, 'comments');
          const commentQ = query(commentsRef, where('productId', 'in', sellerProductIds));
          const unsubscribeComments = onSnapshot(commentQ, (commentSnap) => {
            const reviewList = commentSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setReviews(reviewList.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)));
            setLoading(false);
          }, (err) => {
            setError('Failed to load reviews.');
            setLoading(false);
            console.error(err);
          });

          setProductMap(productData);
          return () => unsubscribeComments();
        }, (err) => {
          setError('Failed to load your products.');
          setLoading(false);
          console.error(err);
        });

        return () => unsubscribeProducts();
      } catch (err) {
        setError('Unexpected error.');
        setLoading(false);
        console.error(err);
      }
    };

    fetchReviews();
  }, [user?.uid, user?.userType]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading your reviews...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-medium text-gray-900 mb-6">Customer Reviews</h1>
      {reviews.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <MessageCircle className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600">No reviews yet for your products.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="border border-gray-200 rounded-lg p-5 bg-white">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {productMap[review.productId]?.name || 'Unknown Product'}
                  </h3>
                  <p className="text-sm text-gray-500">by {review.userName}</p>
                </div>
                <span className="text-xs text-gray-500">
                  {review.timestamp?.toDate().toLocaleDateString()}
                </span>
              </div>
              <div className="flex items-center gap-1 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    fill={i < review.rating ? 'currentColor' : 'none'}
                    className="text-yellow-500"
                  />
                ))}
              </div>
              <p className="text-gray-700 mt-2">{review.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SellerReviewsPage;